/**
 * @props label: Label above the select dropdown (string)
 * @props name: Name for the element. Name will be same as the property that we want to change (string)
 * @props value: Default selected value of the dropdown
 * @props onSelectCb: Callback function after the option is selected from the dropdown. Callback function will recieve two parameters. First is the name of the element and second is the selectedOption object.
 * 
 * @props iconName: Name of the icon to be displayed on the card. (string)
 * @props familyName: Family name of the icon (string). 'fa' | 'fas' | 'fab' | 'vec'
 * @props isOpen: to handle the default open state of the dropdown (boolean)

* @props options: array of objects to render the dropdown items. Object consist of label and value. 
 *          Example: [{ label: "Option 1", value: "option 1" }, { label: "Option 2", value: "option 2" }]
 
* @props suffixProps: Object containing icon or text. Set icon if we want to render the icon and set text if we want to render any text. 
                      suffixProps will set the icon or text at the right side of the text field
                      Example: { icon: VEC_ICON_NAME.ICON_DROPDOWN_CARET } or { text: "+91" } 
 * 
 * @props prefixProps: Object containing icon or text. Set icon if we want to render the icon and set text if we want to render any text. 
                      prefixProps will set the icon or text at the left side of the text field
                      Example: { icon: VEC_ICON_NAME.ICON_DROPDOWN_CARET } or { text: "+91" } 
 */

import React, { useEffect, useRef, useState } from 'react';
import TextInput from '../../input/textinput/TextInput';
import { VEC_ICON_NAME } from '../../icons/constants';
import TxDropdown from '../txdropdown/TxDropdown';
import { DropdownItem } from 'reactstrap';
import styles from "./SelectDropdown.styles"
import { ShowForAllowedPermissionsOnly } from '../../../businesslogic/wrappers/ShowForAllowedPermissionsOnly';
import { KEYBOARD_EVENT_KEYS } from '../../constants';


export default function SelectDropdown({
    label = "",
    options = [],
    placeHolder = "",
    name = "",
    value = { label: "", value: "" },
    isOpen = false,
    onSelectCb = () => { },
    suffixProps = { icon: VEC_ICON_NAME.ICON_DROPDOWN_CARET, clearIcon: VEC_ICON_NAME.CLOSE },
    prefixProps = {},
    hideBorder = false,
    customInputStyles = {},
    rules = {},
    errors = {},
    formSubmitted,
    clearSearchTextCb = () => { },
    onDropdownTouchedToBottomCb = () => { },
    onSearchInputChangeCb = () => { },
    disabled = false,
    isClearable = true,
    dropdownMenuStyle = {},
    dropdownContainerStyles = {},
    searchInitials = true,
    inputType ='text',
    tabIndex="0"
}) {

    const [isDropdownOpen, setIsDropdownOpen] = useState(isOpen);
    const [searchText, setSearchText] = useState(value.label ?? "");
    const [filteredOptions, setFilteredOptions] = useState([])
    const [focusedIndex, setFocusedIndex] = useState(-1);

    const inputRef = useRef(null);
    const dropdownOptionsConainterRef = useRef()

    useEffect(() => {
        if (isDropdownOpen) {
            inputRef.current.focus();
        } else {
            setSearchText("")
            clearSearchTextCb()
        }
    }, [isDropdownOpen])

    useEffect(() => {
        if (dropdownOptionsConainterRef.current && focusedIndex !== -1) {
            const focusedItem = dropdownOptionsConainterRef?.current?.children?.length && dropdownOptionsConainterRef.current.children[focusedIndex];
            if (focusedItem) {
                focusedItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    }, [focusedIndex, dropdownOptionsConainterRef]);

    useEffect(() => {
        let filteredOptions = []
        
        if (!searchInitials) {
            filteredOptions = options.filter((opt) => opt.label.toLowerCase().includes(searchText))
        } else {
            filteredOptions = options.filter(opt => opt.label.toLowerCase().startsWith(searchText.toLowerCase()))
        }
        setFilteredOptions(filteredOptions)
    }, [options, searchInitials, searchText])

    const toggle = () => {
        if (!disabled) {
            setIsDropdownOpen(prev => !prev)
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === KEYBOARD_EVENT_KEYS.ARROWDOWN && isDropdownOpen) {
            setFocusedIndex(prevIndex => Math.min(prevIndex + 1, filteredOptions.length - 1));
        } else if (event.key === KEYBOARD_EVENT_KEYS.ARROWUP && isDropdownOpen) {
            setFocusedIndex(prevIndex => Math.max(prevIndex - 1, -1));
        } else if (event.key === KEYBOARD_EVENT_KEYS.ENTER && focusedIndex !== -1) {
            onChangehandler(filteredOptions[focusedIndex]);
        }
    };

    const onChangehandler = (option, idx) => {
        setSearchText("")
        onSelectCb(name, option);
        setIsDropdownOpen(false);
        if (idx) {
            setFocusedIndex(idx)
        }
    };

    const DropdownToggleComponent = (
        <>
            <TextInput
                label={label}
                name={name}
                placeHolder={placeHolder}
                value={searchText ? searchText : value.label}
                prefixProps={prefixProps}
                suffixProps={{ ...suffixProps, clearIcon: VEC_ICON_NAME.CLOSE, suffixClearIconCb: () => onChangehandler({ label: "", value: "" }) }}
                customStyles={{ ...styles.input, ...(disabled ? styles.disabledStyle : {}), ...customInputStyles }}
                hideBorder={hideBorder}
                rules={rules}
                errors={errors}
                formSubmitted={formSubmitted}
                onChangeCb={(e) => {
                    if(e.type == "change" && e?.nativeEvent?.data){
                        setSearchText(e?.nativeEvent?.data)
                    }
                    setIsDropdownOpen(true)}
                }
                disabled={disabled}
                isClearable={isClearable}
            />
        </>
    )

    const DropdownOptionsComponent = <div ref={dropdownOptionsConainterRef}>

        {filteredOptions.map((opt, idx) => (
            <ShowForAllowedPermissionsOnly toCheck={opt?.permissions ?? []}>
                <DropdownItem onClick={() => onChangehandler(opt, idx)} style={{ ...styles.dropdownItem, ...(opt?.value === value?.value ? styles.dropdownActiveItem : focusedIndex === idx ? styles.dropdownHoveredItem : {}) }}>{opt.label}</DropdownItem>
            </ShowForAllowedPermissionsOnly>
        ))}
    </div>

    return (
        <>
            <input
                ref={inputRef}
                value={searchText}
                onChange={(e) => {
                    if(!isDropdownOpen){
                        setIsDropdownOpen(true)
                    }
                    setSearchText(e.target.value)
                    onSearchInputChangeCb(e)
                }}
                type={inputType}
                style={styles.searchInput}
                disabled={disabled}
                onKeyDown={handleKeyDown}
                tabIndex={tabIndex}
            />

            <TxDropdown
                isOpen={isDropdownOpen}
                toggleCb={toggle}
                dropdownMenuStyle={{ ...styles.dropdownMenu, ...dropdownMenuStyle }}
                DropdownToggleComponent={DropdownToggleComponent}
                DropdownMenuComponent={filteredOptions.length ? DropdownOptionsComponent : undefined}
                onDropdownTouchedToBottomCb={onDropdownTouchedToBottomCb}
                dropdownContainerStyles={dropdownContainerStyles}
            />
        </>
    );
}